import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORY_META, CATEGORY_ORDER, TIER_STYLE } from "@/lib/constants";
import { Star, Users, Zap, TrendingUp, BadgeCheck, Phone, MapPin, Clock, Crown } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState({ business_name: "", category: "mechanics", description: "", phone: "", city: "", address: "", lat: 52.5, lng: -1.9, services: "", image_url: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === false) { openAuth("login"); navigate("/"); return; }
    if (user && user.role !== "business") { toast.error("Business account required"); navigate("/"); return; }
    if (user) loadAll();
    // eslint-disable-next-line
  }, [user]);

  const loadAll = async () => {
    const [p, s, l] = await Promise.all([
      api.get("/my/provider"), api.get("/my/stats"), api.get("/my/leads"),
    ]);
    setProvider(p.data);
    setStats(s.data);
    setLeads(l.data);
    if (p.data) {
      setForm({
        business_name: p.data.business_name, category: p.data.category, description: p.data.description || "",
        phone: p.data.phone, city: p.data.city, address: p.data.address || "",
        lat: p.data.lat, lng: p.data.lng, services: (p.data.services || []).join(", "), image_url: p.data.image_url || "",
      });
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng),
        services: form.services.split(",").map((s) => s.trim()).filter(Boolean) };
      const { data } = await api.post("/my/provider", payload);
      setProvider(data);
      toast.success("Listing saved!");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return <div className="text-center py-20 text-slate-500">Loading...</div>;

  const tier = provider?.tier || "free";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head font-black text-3xl sm:text-4xl text-slate-50">Business Dashboard</h1>
          <p className="text-slate-400 text-sm">Manage your MotorPro listing, plan and leads.</p>
        </div>
        <span className={`text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider ${TIER_STYLE[tier]}`}>
          {tier === "gold" && <Crown size={12} className="inline mr-1" />} {tier} plan
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <StatCard icon={Users} label="Total leads" value={stats?.total_leads ?? 0} />
        <StatCard icon={Zap} label="New leads" value={stats?.new_leads ?? 0} accent />
        <StatCard icon={Star} label="Rating" value={provider?.rating || "—"} />
        <StatCard icon={TrendingUp} label="Reviews" value={provider?.review_count ?? 0} />
      </div>

      <Tabs defaultValue="overview" className="mt-8">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger data-testid="business-dashboard-tab-overview" value="overview" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950">Listing</TabsTrigger>
          <TabsTrigger data-testid="business-dashboard-tab-subscription" value="subscription" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950">Subscription</TabsTrigger>
          <TabsTrigger data-testid="business-dashboard-tab-leads" value="leads" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950">Leads {leads.length > 0 && <span className="ml-1 bg-red-500 text-white text-[10px] px-1.5 rounded-full">{leads.length}</span>}</TabsTrigger>
        </TabsList>

        {/* Listing editor */}
        <TabsContent value="overview" className="mt-5">
          <form onSubmit={save} className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <h3 className="font-head font-bold text-xl text-slate-50 mb-1">{provider ? "Edit your listing" : "Create your listing"}</h3>
              <p className="text-slate-400 text-sm mb-3">This is what customers see on the map and directory.</p>
            </div>
            <Field label="Business name"><Input data-testid="listing-name-input" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="bg-slate-800 border-slate-700" /></Field>
            <Field label="Service category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="listing-category-select" className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  {CATEGORY_ORDER.map((id) => <SelectItem key={id} value={id}>{CATEGORY_META[id].name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Phone"><Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="+44 7..." /></Field>
            <Field label="City / area"><Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-slate-800 border-slate-700" /></Field>
            <Field label="Latitude"><Input type="number" step="any" required value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="bg-slate-800 border-slate-700" /></Field>
            <Field label="Longitude"><Input type="number" step="any" required value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className="bg-slate-800 border-slate-700" /></Field>
            <Field label="Services (comma separated)" full><Input value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="Diagnostics, Brakes, Servicing" /></Field>
            <Field label="Image URL (optional)" full><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-slate-800 border-slate-700" placeholder="https://..." /></Field>
            <Field label="Description" full><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-slate-800 border-slate-700" rows={3} /></Field>
            <div className="md:col-span-2">
              <Button data-testid="listing-save-button" type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl h-11 px-8">
                {saving ? "Saving..." : provider ? "Save changes" : "Create listing"}
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* Subscription */}
        <TabsContent value="subscription" className="mt-5">
          <div className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-slate-400 text-sm">Current plan</p>
                <p className="font-head font-extrabold text-3xl text-slate-50 capitalize flex items-center gap-2">
                  {tier} {provider?.verified && <BadgeCheck size={22} className="text-blue-400" />}
                </p>
              </div>
              <Button onClick={() => navigate("/pricing")} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl">
                {tier === "gold" ? "Manage plan" : "Upgrade plan"}
              </Button>
            </div>
            <div className="mt-4 grid sm:grid-cols-3 gap-3">
              {["bronze", "silver", "gold"].map((t) => (
                <div key={t} className={`rounded-xl border p-4 text-center ${tier === t ? "border-amber-500 bg-amber-500/10" : "border-slate-800 bg-slate-800/30"}`}>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full uppercase ${TIER_STYLE[t]}`}>{t}</span>
                  <p className="text-2xl font-head font-black mt-2">£{t === "bronze" ? 29 : t === "silver" ? 59 : 99}<span className="text-sm text-slate-500">/mo</span></p>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Leads */}
        <TabsContent value="leads" className="mt-5">
          {!provider ? (
            <div className="text-center py-12 text-slate-500">Create a listing first to receive leads.</div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users size={40} className="mx-auto mb-3 opacity-40" />
              No call-out requests yet. Upgrade your plan for higher visibility and more leads.
            </div>
          ) : (
            <div className="space-y-3">
              {leads.map((l) => (
                <div key={l.id} data-testid={`lead-${l.id}`} className="bg-slate-900/70 border border-slate-800 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-50">{l.customer_name}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${l.urgency === "emergency" ? "bg-red-500/20 text-red-400" : l.urgency === "urgent" ? "bg-amber-500/20 text-amber-400" : "bg-slate-700 text-slate-300"}`}>{l.urgency}</span>
                    </div>
                    <div className="text-sm text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="flex items-center gap-1"><MapPin size={13} /> {l.location}</span>
                      {l.vehicle_reg && <span>{l.vehicle_reg} · {l.vehicle_model}</span>}
                      {l.notes && <span className="italic">"{l.notes}"</span>}
                    </div>
                  </div>
                  <a href={`tel:${l.phone}`}>
                    <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl">
                      <Phone size={15} className="mr-1.5" /> {l.phone}
                    </Button>
                  </a>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-2xl border p-5 ${accent ? "bg-amber-500/10 border-amber-500/40" : "bg-slate-900/70 border-slate-800"}`}>
    <Icon size={20} className={accent ? "text-amber-400" : "text-slate-400"} />
    <p className="font-head font-black text-3xl text-slate-50 mt-2">{value}</p>
    <p className="text-xs text-slate-400">{label}</p>
  </div>
);

const Field = ({ label, children, full }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <Label className="text-slate-300 text-xs">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
